import React, { useEffect } from "react";
import styled from "styled-components";
import { CloudDownloadIcon, CheckCircleIcon } from "./IconComponents";
import { useTranslation, Trans } from "react-i18next";
import { HashLink } from "react-router-hash-link";
import SEO from "./SEO";

const Dynamics365Section = styled.section`
  padding: 5rem 0;
  background-color: var(--light-background);
`;

const Dynamics365Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  margin-bottom: 5rem;
  color: var(--text-color);
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  gap: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }

  &:nth-child(even) {
    flex-direction: row-reverse;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureImage = styled.div`
  flex: 1;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const FeatureDescription = styled.p`
  margin-bottom: 1.5rem;
  color: var(--text-color);
  line-height: 1.6;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 0.8rem;

  svg {
    color: var(--primary-color);
    margin-right: 0.5rem;
    flex-shrink: 0;
  }
`;

const CTAContainer = styled.div`
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  padding: 3rem;
  background-color: var(--card-background);
  border-radius: 8px;
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CTADescription = styled.p`
  max-width: 700px;
  margin: 0 auto 2rem;
  color: var(--text-color);
`;

const SectionTitleQuestion = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 0rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const CTAButton = styled(HashLink)`
  display: block;
  width: 50%;
  margin: 2rem auto 0;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 4px;
  background-color: var(--primary-color);
  color: var(--light-text-color);
  font-size: 1.05rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Dynamics365: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = t("dynamics365.features", { returnObjects: true }) as any[];

  // Images for the features
  const featureImages = [
    "/images/unsplash/dynamics365-1.jpg",
    "/images/unsplash/dynamics365-2.jpg",
    "/images/unsplash/dynamics365-3.jpg",
  ];

  return (
    <Dynamics365Section id="dynamics365">
      <SEO
        title="Microsoft Dynamics 365 Beratung & Implementierung"
        description="Professionelle Dynamics 365 Beratung: Implementierung, Anpassung und Optimierung für effiziente Geschäftsprozesse. Jetzt Expertise von Byte Forge nutzen."
        path="/dynamics365"
        keywords="Dynamics 365, Microsoft Dynamics, CRM, ERP, Dynamics 365 Beratung, Dynamics 365 Implementierung"
      />
      <Dynamics365Container>
        <SectionTitle>
          <Trans i18nKey="dynamics365.title">
            Dynamics 365 <span>Expertise</span>
          </Trans>
        </SectionTitle>
        <SectionSubtitle>{t("dynamics365.subtitle")}</SectionSubtitle>

        <ContentWrapper>
          {features.map((feature: any, index: number) => (
            <FeatureRow key={index}>
              <FeatureContent>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
                <FeatureList>
                  {feature.benefits.map(
                    (benefit: string, benefitIndex: number) => (
                      <FeatureItem key={benefitIndex}>
                        <CheckCircleIcon /> {benefit}
                      </FeatureItem>
                    ),
                  )}
                </FeatureList>
              </FeatureContent>
              <FeatureImage>
                <img src={featureImages[index]} alt={feature.title} />
              </FeatureImage>
            </FeatureRow>
          ))}
        </ContentWrapper>

        <CTAContainer>
          <SectionTitleQuestion>
            <Trans>
              Bereit, Ihr Unternehmen <span>zu transformieren?</span>
            </Trans>
          </SectionTitleQuestion>
          <CTADescription>{t("dynamics365.cta.description")}</CTADescription>
          <CTAButton smooth to="/#contact">
            Kostenloses Erstgespräch
          </CTAButton>
        </CTAContainer>
      </Dynamics365Container>
    </Dynamics365Section>
  );
};

export default Dynamics365;
